import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Section,
  Eyebrow,
  Headline,
  Lead,
} from '../components/iss/IssPrimitives';
import apiService, { type PocSummaryResponse } from '../service/ApiService';
import { EmptyState, PhasePill, PhaseTabs, SearchField } from './PocListParts';
import {
  formatDate,
  formatLabel,
  isOwnPoc,
  matchesPocSearch,
  resumePathForPhase,
  shortId,
  type PhaseFilter,
} from './pocListUtils';
import './MyPocsPage.css';

const AllPocsPage: React.FC = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const account = accounts[0];
  const signedInEmail =
    account?.username ||
    (typeof account?.idTokenClaims?.email === 'string'
      ? account.idTokenClaims.email
      : undefined);

  const [items, setItems] = useState<PocSummaryResponse[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>('ALL');
  const [idQuery, setIdQuery] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [requestorQuery, setRequestorQuery] = useState('');

  const openOwnPoc = (poc: PocSummaryResponse) => {
    if (!isOwnPoc(poc, signedInEmail)) return;
    navigate(resumePathForPhase(poc.currentPhase), {
      state: {
        pocId: poc.id,
        title: poc.title,
        businessJustification: poc.title
          ? { title: poc.title }
          : undefined,
      },
    });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await apiService.getAllPocs({ page: 0, size: 50 });
        if (cancelled) return;
        setItems(page.content ?? []);
        setTotalElements(page.totalElements ?? 0);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(
            'Could not load All PoCs. Check that the API is running and you are signed in.'
          );
          setItems([]);
          setTotalElements(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const phaseCounts = useMemo(() => {
    const counts: Record<PhaseFilter, number> = {
      ALL: items.length,
      INITIATION: 0,
      PREPARATION: 0,
      RUNNING: 0,
      EVALUATION: 0,
    };
    for (const poc of items) {
      const phase = poc.currentPhase as PhaseFilter;
      if (phase in counts && phase !== 'ALL') {
        counts[phase] += 1;
      }
    }
    return counts;
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((poc) => {
      if (phaseFilter !== 'ALL' && poc.currentPhase !== phaseFilter) {
        return false;
      }
      return matchesPocSearch(poc, idQuery, nameQuery, requestorQuery);
    });
  }, [items, phaseFilter, idQuery, nameQuery, requestorQuery]);

  const clearFilters = () => {
    setPhaseFilter('ALL');
    setIdQuery('');
    setNameQuery('');
    setRequestorQuery('');
  };

  const hasActiveFilters =
    phaseFilter !== 'ALL' ||
    idQuery.trim() !== '' ||
    nameQuery.trim() !== '' ||
    requestorQuery.trim() !== '';


  return (
    <div className="poc-list">
      <Section theme="dark" spacing="tight" className="poc-list__hero">
        <Eyebrow>PoC library</Eyebrow>
        <Headline as="h1" size="3xl">
          All PoCs
        </Headline>
        <Lead>
          Submitted PoCs across the company (drafts are hidden). You can
          continue only PoCs you own; others are view-only in this list.
        </Lead>
        <PhaseTabs
          active={phaseFilter}
          counts={phaseCounts}
          showClear={hasActiveFilters}
          onSelect={setPhaseFilter}
          onClear={clearFilters}
        />
      </Section>

      <Section theme="light" spacing="tight">
        <div className="poc-list__panel">
          <div className="poc-list__toolbar">
            <div className="poc-list__searches">
              <SearchField
                label="Search for PoC ID"
                placeholder="Search by PoC ID"
                value={idQuery}
                onChange={setIdQuery}
              />
              <SearchField
                label="Search for PoC name"
                placeholder="Search by name"
                value={nameQuery}
                onChange={setNameQuery}
              />
              <SearchField
                label="Search for requestor"
                placeholder="Search by requestor"
                value={requestorQuery}
                onChange={setRequestorQuery}
              />
            </div>
            {!loading && !error ? (
              <p className="poc-list__meta">
                Showing {filtered.length}
                {hasActiveFilters ? ' matching' : ''}
                {totalElements > items.length
                  ? ` (loaded ${items.length} of ${totalElements})`
                  : ` of ${items.length}`}
              </p>
            ) : null}
          </div>

          {loading ? (
            <p className="poc-list__status" role="status">
              Loading All PoCs�
            </p>
          ) : null}

          {error ? (
            <p className="poc-list__error" role="alert">
              {error}
            </p>
          ) : null}

          {!loading && !error && filtered.length === 0 ? (
            items.length === 0 ? (
              <EmptyState title="No submitted PoCs are visible yet." />
            ) : (
              <EmptyState
                title="No PoCs match the current filters."
                action={{ label: 'Clear filters', onClick: clearFilters }}
              />
            )
          ) : null}

          {!loading && !error && filtered.length > 0 ? (
            <div className="poc-list__table-wrap">
              <table className="poc-list__table">
                <thead>
                  <tr>
                    <th scope="col">PoC name</th>
                    <th scope="col">Requestor</th>
                    <th scope="col">Phase</th>
                    <th scope="col">Status</th>
                    <th scope="col">Domain</th>
                    <th scope="col">Updated</th>
                    <th scope="col">
                      <span className="poc-list__sr-only">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((poc) => {
                    const own = isOwnPoc(poc, signedInEmail);
                    return (
                      <tr
                        key={poc.id}
                        className={
                          own ? 'poc-list__row' : 'poc-list__row poc-list__row--readonly'
                        }
                        tabIndex={own ? 0 : undefined}
                        role={own ? 'link' : undefined}
                        aria-label={
                          own
                            ? `Continue ${poc.title || 'PoC'} in ${formatLabel(poc.currentPhase)}`
                            : `${poc.title || 'PoC'} � view only (owned by ${poc.requestorName || poc.requestorEmail || 'another requestor'})`
                        }
                        onClick={own ? () => openOwnPoc(poc) : undefined}
                        onKeyDown={
                          own
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openOwnPoc(poc);
                                }
                              }
                            : undefined
                        }
                      >
                        <td>
                          <span className="poc-list__name">{poc.title || '�'}</span>
                          <span className="poc-list__id" title={poc.id}>
                            {shortId(poc.id)}
                          </span>
                        </td>
                        <td>
                          <span className="poc-list__requestor">
                            {poc.requestorName || '�'}
                            {own ? <span className="poc-list__you">You</span> : null}
                          </span>
                          {poc.requestorEmail ? (
                            <span className="poc-list__id">{poc.requestorEmail}</span>
                          ) : null}
                        </td>
                        <td>
                          <PhasePill phase={poc.currentPhase} />
                        </td>
                        <td className="poc-list__muted">{formatLabel(poc.currentStatus)}</td>
                        <td className="poc-list__muted">{formatLabel(poc.businessDomain)}</td>
                        <td className="poc-list__muted">{formatDate(poc.updatedAt)}</td>
                        <td className="poc-list__action-cell">
                          {own ? (
                            <span className="poc-list__continue" aria-hidden>
                              Continue ?
                            </span>
                          ) : (
                            <span className="poc-list__readonly">View only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Section>
    </div>
  );
};

export default AllPocsPage;
